import { ApiError, type StoreTicket } from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { slaOverdue, TicketComposer, TicketStatusChip, TicketThread } from "@khazzin/ui/patterns";
import {
	DetailLayout,
	ErrorState,
	KeyValueList,
	NotFoundState,
	PageHeader,
} from "@khazzin/ui/patterns/page";
import { SkeletonText } from "@khazzin/ui/primitives";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { linkedPath, ticketsKey } from "./tickets";

/**
 * One ticket (EP10-S12, DA-10-11): what it is about, its thread with Khazzin
 * support, and a reply box until it is closed. An accepted claim shows the
 * credited amount, and whether Finance has posted it yet.
 */
export function TicketDetailPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const { ticketId = "" } = useParams();
	const { store, context } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const key = [...ticketsKey(storeId), "detail", ticketId];

	const detail = useQuery({
		queryKey: key,
		queryFn: ({ signal }) => api.getPortalTicket(ticketId, { signal }),
		enabled: storeId !== "",
		retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
	});

	if (detail.error instanceof ApiError && detail.error.status === 404) {
		return <NotFoundState t={t} />;
	}
	if (detail.isError) {
		return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} t={t} />;
	}
	if (detail.data === undefined) {
		return <SkeletonText lines={6} />;
	}

	const { ticket, messages } = detail.data;
	const at = (iso: string) => formatDateTime(iso, { locale, timeZone });
	const money = (minor: number) =>
		formatMoney({ amountMinor: minor, currency: ticket.currency ?? "USD" }, locale);

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={ticket.number}
				backTo="/tickets"
				backLabel={t("portal.tickets.back")}
				chip={<TicketStatusChip status={ticket.status} t={t} />}
				subtitle={ticket.subject}
			/>
			<CreditBanner ticket={ticket} money={money} />
			<DetailLayout
				rail={
					<section aria-label={t("portal.tickets.detail.summary")} className="card p-4">
						<KeyValueList rows={summaryRows(ticket, t, at, money)} />
					</section>
				}
			>
				<section aria-labelledby="ticket-thread" className="flex flex-col gap-4 card p-4">
					<h2 id="ticket-thread" className="text-[15px] font-bold text-fg">
						{t("tickets.thread.label")}
					</h2>
					<TicketThread messages={messages} mine="store" t={t} formatTime={at} />
					{ticket.status !== "closed" && (
						<TicketComposer
							t={t}
							files={{ api, storeId }}
							onSend={(body, attachmentIds) =>
								api
									.replyPortalTicket(ticket.id, {
										body,
										...(attachmentIds.length > 0 ? { attachmentIds } : {}),
									})
									.then((next) => {
										queryClient.setQueryData(key, next);
										void queryClient.invalidateQueries({
											queryKey: [...ticketsKey(storeId), "list"],
										});
									})
							}
						/>
					)}
				</section>
			</DetailLayout>
		</div>
	);
}

function summaryRows(
	ticket: StoreTicket,
	t: ReturnType<typeof useLocale>["t"],
	at: (iso: string) => string,
	money: (minor: number) => string,
) {
	const link = ticket.linkedEntity;
	const to = link ? linkedPath(link.kind, link.id) : null;
	const linkText = link ? `${t(`tickets.link.${link.kind}`)} ${link.label ?? link.id}` : "—";
	return [
		{ label: t("portal.tickets.columns.type"), value: t(`tickets.type.${ticket.type}`) },
		{
			label: t("portal.tickets.columns.linked"),
			value: to === null ? linkText : <Link to={to}>{linkText}</Link>,
		},
		...(ticket.claimedMinor
			? [{ label: t("portal.tickets.fields.claimed"), value: money(ticket.claimedMinor) }]
			: []),
		{
			label: t("portal.tickets.columns.sla"),
			value: slaOverdue(ticket) ? (
				<span className="font-semibold text-danger-fg">
					{t("tickets.sla.overdue")} · {at(ticket.slaDueAt)}
				</span>
			) : (
				at(ticket.slaDueAt)
			),
		},
		{ label: t("portal.tickets.detail.opened"), value: at(ticket.createdAt) },
		{ label: t("portal.tickets.columns.updated"), value: at(ticket.updatedAt) },
	];
}

/** A decided claim: credited (posted or awaiting Finance), or declined. */
function CreditBanner({
	ticket,
	money,
}: {
	ticket: StoreTicket;
	money: (minor: number) => string;
}) {
	const { t } = useLocale();
	if (ticket.claimDecision === "rejected") {
		return (
			<p role="status" className="rounded-md bg-neutral-bg px-4 py-3 text-[13px] text-neutral-fg">
				{t("portal.tickets.credit.rejected")}
			</p>
		);
	}
	if (ticket.claimDecision !== "accepted" || !ticket.creditedMinor) {
		return null;
	}
	const posted = ticket.creditStatus === "POSTED";
	return (
		<p
			role="status"
			className={
				posted
					? "rounded-md bg-success-bg px-4 py-3 text-[13px] text-success-fg"
					: "rounded-md bg-info-bg px-4 py-3 text-[13px] text-info-fg"
			}
		>
			{t(posted ? "portal.tickets.credit.posted" : "portal.tickets.credit.pending", {
				amount: money(ticket.creditedMinor),
			})}
		</p>
	);
}
